import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Plus, Trash2, GripVertical, Save, Image as ImgIcon, X, Star, Type, Heading } from "lucide-react";
import { toast } from "sonner";
import { Checkbox } from "@/components/ui/checkbox";

const TYPES = [
  { v: "pass_fail", l: "OK / Defeito" },
  { v: "single_choice", l: "Escolha Única (Radio)" },
  { v: "multi_choice", l: "Múltipla Escolha" },
  { v: "numeric", l: "Numérico" },
  { v: "text", l: "Texto" },
  { v: "photo", l: "Foto Obrigatória" },
  { v: "signature", l: "Assinatura" },
  { v: "autocomplete", l: "Autocompletar" },
  { v: "rating", l: "Classificação (Estrelas)" },
  { v: "option_table", l: "Tabela de Opções" },
  { v: "header", l: "Cabeçalho (Header)" },
  { v: "paragraph", l: "Parágrafo (Texto Fixo)" },
  { v: "collaborator_select", l: "Colaboradores (Dropdown)" },
  { v: "vehicle_select", l: "Veículos (Dropdown)" },
];

const NEEDS_CHOICES = ["single_choice", "multi_choice", "autocomplete", "option_table"];

const uid = () => Math.random().toString(36).slice(2, 9);

function ImageField({ label, value, onChange, testid }) {
  const onFile = (f) => {
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => onChange(reader.result);
    reader.readAsDataURL(f);
  };
  return (
    <div>
      <Label className="text-xs uppercase tracking-widest text-muted-foreground">{label}</Label>
      <div className="flex items-center gap-3 mt-1">
        {value ? (
          <div className="relative">
            <img src={value} alt={label} className="w-20 h-14 object-contain rounded-md border border-border bg-white" />
            <button type="button" onClick={() => onChange(null)}
              className="absolute -top-1 -right-1 w-5 h-5 bg-destructive text-white rounded-full flex items-center justify-center">
              <X className="w-3 h-3" />
            </button>
          </div>
        ) : (
          <div className="w-20 h-14 rounded-md border border-dashed border-border flex items-center justify-center">
            <ImgIcon className="w-5 h-5 text-muted-foreground" />
          </div>
        )}
        <label className="cursor-pointer">
          <input type="file" accept="image/*" className="hidden" data-testid={testid}
            onChange={(e) => onFile(e.target.files?.[0])} />
          <span className="text-xs px-3 py-1.5 rounded-md bg-secondary hover:bg-primary hover:text-primary-foreground transition-colors">
            {value ? "Trocar" : "Enviar"}
          </span>
        </label>
      </div>
    </div>
  );
}

export default function TemplateBuilder() {
  const { id } = useParams();
  const nav = useNavigate();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [triggerType, setTriggerType] = useState("Manual");
  const [sections, setSections] = useState([{ section: "Nova Seção", questions: [] }]);
  const [permanent, setPermanent] = useState(true);
  const [requireAuth, setRequireAuth] = useState(true);
  const [generateProtocol, setGenerateProtocol] = useState(true);
  const [emailNotifications, setEmailNotifications] = useState("");
  const [leftLogo, setLeftLogo] = useState(null);
  const [rightLogo, setRightLogo] = useState(null);
  const [watermark, setWatermark] = useState(null);
  const [fuelAction, setFuelAction] = useState("none");
  const [allowedSubRoles, setAllowedSubRoles] = useState([]);

  useEffect(() => {
    if (!id) return;
    api.get(`/templates/${id}`).then(({ data }) => {
      setTitle(data.title); setDescription(data.description); setTriggerType(data.trigger_type);
      setSections(data.structure_json?.length ? data.structure_json : [{ section: "Nova Seção", questions: [] }]);
      setPermanent(data.permanent !== false);
      setRequireAuth(data.require_auth !== false);
      setGenerateProtocol(data.generate_protocol !== false);
      setEmailNotifications(data.email_notifications || "");
      setLeftLogo(data.left_logo || null);
      setRightLogo(data.right_logo || null);
      setWatermark(data.watermark || null);
      setFuelAction(data.fuel_action || "none");
      setAllowedSubRoles(data.allowed_sub_roles || []);
    });
  }, [id]);

  const addSection = () => setSections([...sections, { section: "Nova Seção", questions: [] }]);
  const rmSection = (i) => setSections(sections.filter((_, x) => x !== i));
  const updateSection = (i, patch) => setSections(sections.map((s, x) => x === i ? { ...s, ...patch } : s));
  const addQuestion = (i, type = "pass_fail") => {
    const base = { id: uid(), label: type === "header" ? "Título da seção" : type === "paragraph" ? "Texto informativo" : "Nova pergunta", type, photo_required: false };
    if (NEEDS_CHOICES.includes(type)) base.choices = ["Opção 1", "Opção 2"];
    if (type === "option_table") { base.rows = ["Item A", "Item B"]; base.columns = ["OK", "Defeito", "N/A"]; }
    if (type === "rating") base.max = 5;
    updateSection(i, { questions: [...sections[i].questions, base] });
  };
  const updateQuestion = (si, qi, patch) => {
    const qs = sections[si].questions.map((q, x) => x === qi ? { ...q, ...patch } : q);
    updateSection(si, { questions: qs });
  };
  const rmQuestion = (si, qi) => updateSection(si, { questions: sections[si].questions.filter((_, x) => x !== qi) });

  const toggleSubRole = (subRole) => {
    setAllowedSubRoles((current) => {
      if (current.includes(subRole)) {
        return current.filter((r) => r !== subRole);
      }

      return [...current, subRole];
    });
  };

  const save = async () => {
    if (!title) return toast.error("Preencha o título do formulário");
    const payload = {
      title, description, trigger_type: triggerType, structure_json: sections,
      permanent, require_auth: requireAuth, generate_protocol: generateProtocol,
      email_notifications: emailNotifications,
      left_logo: leftLogo, right_logo: rightLogo, watermark,
      fuel_action: fuelAction === "none" ? "" : fuelAction,
      allowed_sub_roles: allowedSubRoles,
    };
    try {
      if (id) await api.put(`/templates/${id}`, payload);
      else await api.post("/templates", payload);
      toast.success("Modelo salvo");
      nav("/templates");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="template-builder">
      <PageHeader title={id ? "Editar Modelo" : "Novo Modelo"} subtitle="Construa formulários dinâmicos, seções e componentes"
        actions={<Button onClick={save} className="gap-2" data-testid="save-template"><Save className="w-4 h-4" />Salvar</Button>} />

      <Tabs defaultValue="general" className="mb-6">
        <TabsList>
          <TabsTrigger value="general" data-testid="tab-general">Geral</TabsTrigger>
          <TabsTrigger value="settings" data-testid="tab-settings">Configurações</TabsTrigger>
          <TabsTrigger value="branding" data-testid="tab-branding">Identidade Visual</TabsTrigger>
        </TabsList>

        <TabsContent value="general">
          <Card className="p-5">
            <div className="grid md:grid-cols-2 gap-4">
              <div><Label>Título do Formulário*</Label><Input data-testid="tpl-title" value={title} onChange={(e) => setTitle(e.target.value)} /></div>
              <div>
                <Label>Tipo de Gatilho</Label>
                <Select value={triggerType} onValueChange={setTriggerType}>
                  <SelectTrigger data-testid="tpl-trigger"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Fleetrun Alert">Alerta Fleetrun</SelectItem>
                    <SelectItem value="Scheduled">Agendado</SelectItem>
                    <SelectItem value="Manual">Manual</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Ação de Combustível</Label>
                <Select value={fuelAction} onValueChange={setFuelAction}>
                  <SelectTrigger data-testid="tpl-fuel"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Nenhuma</SelectItem>
                    <SelectItem value="transfer_to_comboio">Transferência Estacionário → Comboio</SelectItem>
                    <SelectItem value="field_refuel">Abastecimento em Campo</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1">Ao enviar, o sistema movimentará o estoque automaticamente.</p>
              </div>
              <div className="md:col-span-2"><Label>Descrição</Label><Textarea data-testid="tpl-desc" value={description} onChange={(e) => setDescription(e.target.value)} rows={2} /></div>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="settings">
          <Card className="p-5 space-y-4">
            <div className="flex items-center justify-between gap-4 p-3 rounded-md border border-border">
              <div>
                <div className="font-semibold text-sm">Formulário Permanente</div>
                <div className="text-xs text-muted-foreground">Se desativado, o formulário expira após uma execução.</div>
              </div>
              <Switch checked={permanent} onCheckedChange={setPermanent} data-testid="switch-permanent" />
            </div>
            <div className="flex items-center justify-between gap-4 p-3 rounded-md border border-border">
              <div>
                <div className="font-semibold text-sm">Exigir Autenticação</div>
                <div className="text-xs text-muted-foreground">Obriga login/senha para responder o formulário.</div>
              </div>
              <Switch checked={requireAuth} onCheckedChange={setRequireAuth} data-testid="switch-require-auth" />
            </div>
            <div className="flex items-center justify-between gap-4 p-3 rounded-md border border-border">
              <div>
                <div className="font-semibold text-sm">Gerar Número de Protocolo</div>
                <div className="text-xs text-muted-foreground">Emite um número único no envio (formato FC-AAAAMMDD-000001).</div>
              </div>
              <Switch checked={generateProtocol} onCheckedChange={setGenerateProtocol} data-testid="switch-protocol" />
            </div>
            <div className="p-3 rounded-md border border-border space-y-3">
            <div>
              <div className="font-semibold text-sm">
                Quem pode executar?
              </div>

              <div className="text-xs text-muted-foreground">
                Se nenhum perfil for selecionado, o formulário ficará disponível para todos os operadores.
              </div>
            </div>

            <div className="grid sm:grid-cols-3 gap-3">

              <label className="flex items-center gap-2 p-3 rounded-md border border-border cursor-pointer hover:bg-secondary/50">
                <Checkbox
                  checked={allowedSubRoles.includes("motorista")}
                  onCheckedChange={() =>
                    toggleSubRole("motorista")
                  }
                />

                <span className="text-sm font-medium">
                  Motorista
                </span>
              </label>

              <label className="flex items-center gap-2 p-3 rounded-md border border-border cursor-pointer hover:bg-secondary/50">
                <Checkbox
                  checked={allowedSubRoles.includes("mecanico")}
                  onCheckedChange={() =>
                    toggleSubRole("mecanico")
                  }
                />

                <span className="text-sm font-medium">
                  Mecânico
                </span>
              </label>

              <label className="flex items-center gap-2 p-3 rounded-md border border-border cursor-pointer hover:bg-secondary/50">
                <Checkbox
                  checked={allowedSubRoles.includes("abastecedor")}
                  onCheckedChange={() =>
                    toggleSubRole("abastecedor")
                  }
                />

                <span className="text-sm font-medium">
                  Abastecedor
                </span>
              </label>

            </div>
          </div>
            <div>
              <Label>Envio automático de e-mail para</Label>
              <Input placeholder="email1@empresa.com, email2@empresa.com" value={emailNotifications} onChange={(e) => setEmailNotifications(e.target.value)} data-testid="tpl-emails" />
              <p className="text-xs text-muted-foreground mt-1">Separe múltiplos e-mails por vírgula. Uma cópia da inspeção é enviada ao ser finalizada.</p>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="branding">
          <Card className="p-5 space-y-4">
            <p className="text-xs text-muted-foreground">Logos e marca d'água aparecem no cabeçalho do formulário e no PDF exportado.</p>
            <div className="grid md:grid-cols-3 gap-4">
              <ImageField label="Logo Esquerda" value={leftLogo} onChange={setLeftLogo} testid="upload-left-logo" />
              <ImageField label="Logo Direita" value={rightLogo} onChange={setRightLogo} testid="upload-right-logo" />
              <ImageField label="Marca d'Água / Fundo" value={watermark} onChange={setWatermark} testid="upload-watermark" />
            </div>
          </Card>
        </TabsContent>
      </Tabs>

      <div className="space-y-4">
        {sections.map((s, si) => (
          <Card key={si} className="p-5" data-testid={`section-${si}`}>
            <div className="flex items-center gap-2 mb-4">
              <GripVertical className="w-4 h-4 text-muted-foreground" />
              <Input value={s.section} onChange={(e) => updateSection(si, { section: e.target.value })} className="font-display font-bold text-lg" data-testid={`section-name-${si}`} />
              <Button variant="ghost" size="sm" onClick={() => rmSection(si)} data-testid={`rm-section-${si}`}><Trash2 className="w-4 h-4 text-destructive" /></Button>
            </div>
            <div className="space-y-2">
              {s.questions.map((q, qi) => {
                const isDisplay = q.type === "header" || q.type === "paragraph";
                const needsChoices = NEEDS_CHOICES.includes(q.type);
                const isTable = q.type === "option_table";
                return (
                  <div key={q.id} className="p-3 rounded-md border border-border space-y-2">
                    <div className="grid grid-cols-12 gap-2 items-center">
                      <div className="col-span-6 flex items-center gap-1">
                        {q.type === "header" && <Heading className="w-4 h-4 text-primary" />}
                        {q.type === "paragraph" && <Type className="w-4 h-4 text-primary" />}
                        {q.type === "rating" && <Star className="w-4 h-4 text-primary" />}
                        {q.type === "paragraph"
                          ? <Textarea rows={2} value={q.label} onChange={(e) => updateQuestion(si, qi, { label: e.target.value })} className="text-sm" data-testid={`q-label-${si}-${qi}`} />
                          : <Input value={q.label} onChange={(e) => updateQuestion(si, qi, { label: e.target.value })} data-testid={`q-label-${si}-${qi}`} />
                        }
                      </div>
                      <Select value={q.type} onValueChange={(v) => {
                        const patch = { type: v };
                        if (NEEDS_CHOICES.includes(v) && !q.choices) patch.choices = ["Opção 1", "Opção 2"];
                        if (v === "option_table") { patch.rows = q.rows || ["Item A"]; patch.columns = q.columns || ["OK", "Defeito"]; }
                        if (v === "rating" && !q.max) patch.max = 5;
                        updateQuestion(si, qi, patch);
                      }}>
                        <SelectTrigger className="col-span-3" data-testid={`q-type-${si}-${qi}`}><SelectValue /></SelectTrigger>
                        <SelectContent>{TYPES.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}</SelectContent>
                      </Select>
                      {!isDisplay ? (
                        <div className="col-span-2 flex items-center gap-2 text-xs">
                          <Switch checked={!!q.photo_required} onCheckedChange={(v) => updateQuestion(si, qi, { photo_required: v })} data-testid={`q-photo-${si}-${qi}`} /> Foto
                        </div>
                      ) : <div className="col-span-2" />}
                      <Button variant="ghost" size="sm" onClick={() => rmQuestion(si, qi)} data-testid={`rm-q-${si}-${qi}`}><Trash2 className="w-4 h-4 text-destructive" /></Button>
                    </div>

                    {q.type === "rating" && (
                      <div className="pl-4 flex items-center gap-2 text-xs">
                        <Label>Máx:</Label>
                        <Input type="number" min={3} max={10} value={q.max || 5} onChange={(e) => updateQuestion(si, qi, { max: Number(e.target.value) })} className="w-20 h-8" data-testid={`q-rating-max-${si}-${qi}`} />
                      </div>
                    )}

                    {isTable && (
                      <div className="pl-4 border-l-2 border-primary/30 space-y-3">
                        <div>
                          <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-1">Linhas (itens)</div>
                          {(q.rows || []).map((r, ri) => (
                            <div key={ri} className="flex gap-2 items-center mb-1">
                              <Input value={r} onChange={(e) => {
                                const rs = [...q.rows]; rs[ri] = e.target.value; updateQuestion(si, qi, { rows: rs });
                              }} className="h-8 text-sm" data-testid={`q-row-${si}-${qi}-${ri}`} />
                              <Button variant="ghost" size="sm" onClick={() => updateQuestion(si, qi, { rows: q.rows.filter((_, x) => x !== ri) })}><Trash2 className="w-3 h-3 text-destructive" /></Button>
                            </div>
                          ))}
                          <Button variant="outline" size="sm" onClick={() => updateQuestion(si, qi, { rows: [...(q.rows || []), `Item ${(q.rows?.length || 0) + 1}`] })} className="gap-1 h-7 text-xs"><Plus className="w-3 h-3" />Linha</Button>
                        </div>
                        <div>
                          <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-1">Colunas (opções)</div>
                          {(q.columns || []).map((c, ci) => (
                            <div key={ci} className="flex gap-2 items-center mb-1">
                              <Input value={c} onChange={(e) => {
                                const cs = [...q.columns]; cs[ci] = e.target.value; updateQuestion(si, qi, { columns: cs });
                              }} className="h-8 text-sm" data-testid={`q-col-${si}-${qi}-${ci}`} />
                              <Button variant="ghost" size="sm" onClick={() => updateQuestion(si, qi, { columns: q.columns.filter((_, x) => x !== ci) })}><Trash2 className="w-3 h-3 text-destructive" /></Button>
                            </div>
                          ))}
                          <Button variant="outline" size="sm" onClick={() => updateQuestion(si, qi, { columns: [...(q.columns || []), `Col ${(q.columns?.length || 0) + 1}`] })} className="gap-1 h-7 text-xs"><Plus className="w-3 h-3" />Coluna</Button>
                        </div>
                      </div>
                    )}

                    {needsChoices && !isTable && (
                      <div className="pl-4 border-l-2 border-primary/30 space-y-1">
                        <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">Opções</div>
                        {(q.choices || []).map((c, ci) => (
                          <div key={ci} className="flex gap-2 items-center">
                            <Input value={c} onChange={(e) => {
                              const cs = [...(q.choices || [])]; cs[ci] = e.target.value;
                              updateQuestion(si, qi, { choices: cs });
                            }} className="h-8 text-sm" data-testid={`q-choice-${si}-${qi}-${ci}`} />
                            <Button variant="ghost" size="sm" onClick={() => updateQuestion(si, qi, { choices: (q.choices || []).filter((_, x) => x !== ci) })}><Trash2 className="w-3 h-3 text-destructive" /></Button>
                          </div>
                        ))}
                        <Button variant="outline" size="sm" onClick={() => updateQuestion(si, qi, { choices: [...(q.choices || []), `Opção ${(q.choices?.length || 0) + 1}`] })} className="gap-1 h-7 text-xs" data-testid={`add-choice-${si}-${qi}`}>
                          <Plus className="w-3 h-3" />Adicionar opção
                        </Button>
                      </div>
                    )}
                  </div>
                );
              })}
              <div className="flex flex-wrap gap-2 pt-2">
                <Button variant="outline" size="sm" onClick={() => addQuestion(si, "pass_fail")} className="gap-2" data-testid={`add-q-${si}`}><Plus className="w-4 h-4" />Pergunta</Button>
                <Button variant="ghost" size="sm" onClick={() => addQuestion(si, "header")} className="gap-1"><Heading className="w-3.5 h-3.5" />Header</Button>
                <Button variant="ghost" size="sm" onClick={() => addQuestion(si, "paragraph")} className="gap-1"><Type className="w-3.5 h-3.5" />Parágrafo</Button>
                <Button variant="ghost" size="sm" onClick={() => addQuestion(si, "rating")} className="gap-1"><Star className="w-3.5 h-3.5" />Classificação</Button>
                <Button variant="ghost" size="sm" onClick={() => addQuestion(si, "option_table")} className="gap-1"><Plus className="w-3.5 h-3.5" />Tabela</Button>
              </div>
            </div>
          </Card>
        ))}
        <Button variant="outline" onClick={addSection} className="gap-2" data-testid="add-section"><Plus className="w-4 h-4" />Adicionar seção</Button>
      </div>
    </div>
  );
}
